import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const { 
  FiHome, FiCheckSquare, FiCalendar, FiShoppingCart, FiUtensils, 
  FiBox, FiImage, FiFileText, FiAlertCircle, FiSettings, 
  FiLogOut, FiMenu, FiX, FiActivity, FiTrash2, FiShield,
  FiBriefcase, FiUsers, FiCpu, FiMessageCircle, FiTv, FiDroplet,
  FiMaximize2
} = FiIcons;

/**
 * SidebarContent - Die eigentliche Navigationsstruktur
 * Separiert, um sie sowohl in der Desktop-Sidebar als auch im mobilen Drawer zu nutzen.
 */
const SidebarContent = ({ onClose, isAdmin, isSuperAdmin, fuelEnabled }) => {
  const { signOut } = useAuth();
  
  const menuItems = [
    { section: "Home", items: [
      { path: "/dashboard", icon: FiHome, label: "Dashboard" },
      { path: "/tablet", icon: FiMaximize2, label: "Tablet-Modus", highlight: true },
    ]},
    { section: "Planung", items: [
      { path: "/calendar", icon: FiCalendar, label: "Kalender" },
      { path: "/todos", icon: FiCheckSquare, label: "Aufgaben" },
      { path: "/meals", icon: FiUtensils, label: "Menüplan" },
      { path: "/trash", icon: FiTrash2, label: "Müllkalender" },
    ]},
    { section: "Einkauf & Finanzen", items: [
      { path: "/shopping", icon: FiShoppingCart, label: "Einkaufsliste" },
      { path: "/finance", icon: FiActivity, label: "Finanzen" },
    ]},
    { section: "Organisation", items: [
      { path: "/parties", icon: FiUsers, label: "Feierplaner" },
      { path: "/bags", icon: FiBriefcase, label: "Gepackte Tasche" },
      { path: "/assets", icon: FiBox, label: "Inventar" },
      { path: "/documents", icon: FiFileText, label: "Dokumente" },
    ]},
    { section: "Sicherheit & Smart Home", items: [
      { path: "/emergency", icon: FiAlertCircle, label: "Notfall-Center" },
      { path: "/blink", icon: FiTv, label: "Kameras" },
      ...(fuelEnabled ? [{ path: "/fuel", icon: FiDroplet, label: "Spritpreise" }] : []),
    ]},
    { section: "Erinnerungen", items: [
      { path: "/memories", icon: FiImage, label: "Momente" },
    ]},
    { section: "System", items: [
      { path: "/settings", icon: FiSettings, label: "Einstellungen" },
      { path: "/support", icon: FiMessageCircle, label: "Support" },
      ...(isAdmin ? [{ path: "/admin", icon: FiShield, label: "Administration" }] : []),
      ...(isSuperAdmin ? [{ path: "/superadmin", icon: FiCpu, label: "Superadmin" }] : []),
    ]}
  ];

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-300 select-none">
      <div className="p-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/40">
            <SafeIcon icon={FiHome} className="text-white text-xl" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">FamilyPlanner</span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 space-y-8 py-4 custom-scrollbar pb-32">
        {menuItems.map((section, idx) => (
          <div key={idx} className="space-y-1">
            <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              {section.section}
            </h3>
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/30 font-semibold' 
                      : item.highlight 
                        ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                        : 'hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <SafeIcon icon={item.icon} className="text-lg shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="p-4 bg-slate-900/90 backdrop-blur-md border-t border-slate-800 shrink-0 sticky bottom-0">
        <button
          onClick={() => signOut()}
          className="flex items-center gap-3 w-full px-4 py-3 text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-all duration-200"
        >
          <SafeIcon icon={FiLogOut} className="text-lg shrink-0" />
          <span className="font-medium">Abmelden</span>
        </button>
      </div>
    </div>
  );
};

const EnhancedSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAdmin, isSuperAdmin, familyId } = useAuth();
  const location = useLocation();
  const [fuelEnabled, setFuelEnabled] = useState(false);

  // Check feature enablement
  useEffect(() => {
    const checkFuel = async () => {
      if (!familyId) return;
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'tankerkoenig_config')
          .eq('family_id', familyId)
          .maybeSingle();
        
        if (data?.value?.enabled) {
          setFuelEnabled(true);
        }
      } catch (err) {
        console.error("Error checking fuel config:", err);
      }
    };
    checkFuel();
  }, [familyId]);

  // Body Scroll Lock Logic
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none'; // Verhindert Overscroll-Effekte auf iOS
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  // Handle route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const toggleSidebar = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  return (
    <>
      {/* Desktop Sidebar (Fixed) */}
      <aside className="hidden lg:flex flex-col w-64 h-screen fixed left-0 top-0 z-[100] border-r border-slate-800 shadow-xl">
        <SidebarContent 
          isAdmin={isAdmin} 
          isSuperAdmin={isSuperAdmin} 
          fuelEnabled={fuelEnabled}
        />
      </aside>

      {/* Mobile Header (Sticky/Fixed) */}
      <header className="lg:hidden h-16 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 fixed top-0 left-0 right-0 flex items-center justify-between px-4 z-[500] shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiHome} className="text-white text-sm" />
          </div>
          <span className="text-lg font-bold text-white tracking-tight">FamilyPlanner</span>
        </div>
        <button
          onClick={toggleSidebar}
          className="p-2.5 text-slate-300 hover:text-white bg-slate-800/50 rounded-lg transition-all active:scale-95"
          aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
        >
          <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
        </button>
      </header>

      {/* Mobile Menu Portal (Overlay) */}
      {createPortal(
        <AnimatePresence mode="wait">
          {isOpen && (
            <div className="fixed inset-0 z-[99999] lg:hidden overflow-hidden">
              {/* Overlay Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
              />
              
              {/* Drawer Panel */}
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.8 }}
                className="absolute left-0 top-0 bottom-0 w-[280px] h-full bg-slate-900 shadow-2xl overflow-hidden border-r border-slate-800"
              >
                <SidebarContent 
                  onClose={() => setIsOpen(false)} 
                  isAdmin={isAdmin} 
                  isSuperAdmin={isSuperAdmin}
                  fuelEnabled={fuelEnabled}
                />
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default EnhancedSidebar;